import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { pathFor } from "@/lib/routes";
import HeroSlider from "@/components/HeroSlider";
import FeaturedDepartureBanner from "@/components/FeaturedDepartureBanner";
import HomeTrustStrip from "@/components/HomeTrustStrip";
import HomeSectionNav from "@/components/HomeSectionNav";
import DualTimezoneClock from "@/components/DualTimezoneClock";
import SouthMoroccoVirtualExperience from "@/components/SouthMoroccoVirtualExperience";
import { MoroccoLightFilm } from "@/pages/MoroccoLandingPage";
import TripFinder from "@/components/TripFinder";
import HomeAllTripsCatalog from "@/components/HomeAllTripsCatalog";
import Marquee from "@/components/Marquee";
import EmotionalIntro from "@/components/EmotionalIntro";
import WorldMapSection from "@/components/WorldMapSection";
import HomeMoroccoTripMap from "@/components/HomeMoroccoTripMap";
import RuletaPromo from "@/components/RuletaPromo";
import PlannerCta from "@/components/PlannerCta";
import WhyXaluca from "@/components/WhyXaluca";
import FeaturedQuote from "@/components/FeaturedQuote";
import TravelCategories from "@/components/TravelCategories";
import IdealTripWizard from "@/components/IdealTripWizard";
import OurTrips from "@/components/OurTrips";
import AllTripsCarousel from "@/components/AllTripsCarousel";
import StressFreeProcess from "@/components/StressFreeProcess";
import WhatJourneysFeelLike from "@/components/WhatJourneysFeelLike";
import MoroccoVideos from "@/components/MoroccoVideos";
import MoroccoCircuits from "@/components/MoroccoCircuits";
import MapSection from "@/components/MapSection";
import PersonalConsultation, { CommunityCTA } from "@/components/PersonalConsultation";
import ContactForm from "@/components/ContactForm";
import Testimonials from "@/components/Testimonials";
import HomeCategoryCarousel from "@/components/HomeCategoryCarousel";
import PressMentions from "@/components/PressMentions";
import VideoSection from "@/components/VideoSection";
import FoundersSection from "@/components/FoundersSection";
import HomeWelcomeModal from "@/components/HomeWelcomeModal";
import NewsletterSignup from "@/components/NewsletterSignup";
import { HOME_VIDEOS } from "@/lib/sectionVideos";
import {
  SOUTH_TRIPS,
  FULL_TRIPS,
  SHORT_TRIPS,
  NORTH_TRIPS,
  UPCOMING_TRIPS,
} from "@/lib/homeCarousels";

/* Trilingual copy for the five new category sections. */
const COPY = {
  south: {
    eyebrow: { es: "Viajes por el sur de Marruecos", en: "Southern Morocco journeys", fr: "Voyages au Sud du Maroc" },
    title:   { es: "La magia del sur de Marruecos",  en: "The magic of southern Morocco", fr: "La magie du Sud du Maroc" },
    description: {
      es: "Cuatro circuitos cinematográficos por la ruta de las mil kasbahs, el Alto Atlas y las dunas del Erg Chebbi — el alma del Sahara marroquí.",
      en: "Four cinematic circuits along the thousand-kasbahs route, the High Atlas and the Erg Chebbi dunes — the soul of Moroccan Sahara.",
      fr: "Quatre circuits cinématographiques sur la route des mille kasbahs, le Haut Atlas et les dunes de l'Erg Chebbi — l'âme du Sahara marocain.",
    },
    cta: { es: "Ver Sur de Marruecos", en: "View Southern Morocco", fr: "Voir le Sud du Maroc" },
  },
  full: {
    eyebrow: { es: "Marruecos integral", en: "Full Morocco", fr: "Maroc intégral" },
    title:   { es: "Marruecos de norte a sur", en: "Morocco from north to south", fr: "Le Maroc du nord au sud" },
    description: {
      es: "Grandes travesías que conectan medinas imperiales, cordilleras del Atlas y dunas saharianas en un solo viaje.",
      en: "Grand crossings linking imperial medinas, Atlas ranges and Saharan dunes in a single journey.",
      fr: "Grandes traversées reliant médinas impériales, chaînes de l'Atlas et dunes sahariennes en un seul voyage.",
    },
    cta: { es: "Ver Marruecos integral", en: "View full Morocco", fr: "Voir Maroc intégral" },
  },
  short: {
    eyebrow: { es: "Escapadas cortas", en: "Short escapes", fr: "Escapades courtes" },
    title:   { es: "Escapadas cortas por Marruecos", en: "Short escapes in Morocco", fr: "Escapades courtes au Maroc" },
    description: {
      es: "Tres o cuatro días para desconectar — desierto, montaña o medina, sin renunciar a un Marruecos auténtico.",
      en: "Three or four days to disconnect — desert, mountain or medina, without giving up an authentic Morocco.",
      fr: "Trois ou quatre jours pour déconnecter — désert, montagne ou médina, sans renoncer à un Maroc authentique.",
    },
    cta: { es: "Ver escapadas", en: "View short escapes", fr: "Voir escapades" },
  },
  north: {
    eyebrow: { es: "Viajes por el norte de Marruecos", en: "Northern Morocco journeys", fr: "Voyages au Nord du Maroc" },
    title:   { es: "La riqueza del norte de Marruecos", en: "The richness of northern Morocco", fr: "La richesse du Nord du Maroc" },
    description: {
      es: "Ciudades imperiales, medinas vivas, la perla azul del Rif y el estrecho de Gibraltar — el norte cultural y mediterráneo del país.",
      en: "Imperial cities, living medinas, the blue pearl of the Rif and the Strait of Gibraltar — Morocco's cultural Mediterranean north.",
      fr: "Cités impériales, médinas vivantes, la perle bleue du Rif et le détroit de Gibraltar — le nord culturel et méditerranéen du pays.",
    },
    cta: { es: "Ver Norte de Marruecos", en: "View Northern Morocco", fr: "Voir le Nord du Maroc" },
  },
  upcoming: {
    eyebrow: { es: "Salidas en grupo", en: "Group departures", fr: "Départs en groupe" },
    title:   { es: "Próximas salidas en grupo", en: "Upcoming group departures", fr: "Prochains départs en groupe" },
    description: {
      es: "Fechas confirmadas, grupos pequeños (máx. 12 viajeros) y un acompañante Xaluca durante todo el viaje.",
      en: "Confirmed dates, small groups (max. 12 travellers) and a Xaluca host for the entire trip.",
      fr: "Dates confirmées, petits groupes (max. 12 voyageurs) et un accompagnant Xaluca pendant tout le voyage.",
    },
    cta: { es: "Ver todas las salidas", en: "View all departures", fr: "Voir tous les départs" },
  },
};

const DOC_TITLES = {
  es: "Xaluca Tours · Viajes a medida por Marruecos",
  en: "Xaluca Tours · Tailor-made journeys across Morocco",
  fr: "Xaluca Tours · Voyages sur mesure au Maroc",
};

const REVIEWS_CTA = {
  es: "Ver todas las opiniones",
  en: "View all reviews",
  fr: "Voir tous les avis",
};

export default function HomePage() {
  const { lang } = useLanguage();
  useEffect(() => {
    document.title = DOC_TITLES[lang] || DOC_TITLES.es;
  }, [lang]);

  return (
    <div data-testid="home-page">
      {/* The modal remains implemented and reusable; its automatic Home
          trigger is intentionally disabled while the choices are displayed
          permanently inside HomeTrustStrip. */}
      <HomeWelcomeModal autoOpen={false} />
      <HeroSlider />
      <FeaturedDepartureBanner />
      <HomeTrustStrip />
      <HomeSectionNav />
      <HomeMoroccoTripMap />
      <MoroccoLightFilm lang={lang} />
      <DualTimezoneClock />
      <div id="buscador" className="scroll-mt-[172px]">
        <TripFinder />
      </div>
      <SouthMoroccoVirtualExperience />
      <Marquee />
      <EmotionalIntro />
      <div id="destinos" className="scroll-mt-[172px]">
        <WorldMapSection />
      </div>
      <div id="ruleta" className="scroll-mt-[172px]">
        <RuletaPromo />
      </div>
      <PlannerCta />
      <VideoSection {...HOME_VIDEOS.story} testid="home-video-story" />
      <FoundersSection />
      <div id="por-que" className="scroll-mt-[172px]">
        <WhyXaluca />
      </div>
      <FeaturedQuote />
      <div id="viajes" className="scroll-mt-[172px]">
        <TravelCategories />
      </div>
      <IdealTripWizard />
      <OurTrips />
      <HomeAllTripsCatalog initialLimit={8} />
      <AllTripsCarousel />

      {/* Category carousels — quick access to specific trip pages */}
      <HomeCategoryCarousel
        testid="home-south-carousel"
        slotPrefix="home.south"
        eyebrow={COPY.south.eyebrow}
        title={COPY.south.title}
        description={COPY.south.description}
        ctaLabel={COPY.south.cta}
        ctaRouteId="tourSouth"
        trips={SOUTH_TRIPS}
        tone="sand"
        accent="#C16542"
      />

      <HomeCategoryCarousel
        testid="home-full-carousel"
        slotPrefix="home.full"
        eyebrow={COPY.full.eyebrow}
        title={COPY.full.title}
        description={COPY.full.description}
        ctaLabel={COPY.full.cta}
        ctaRouteId="tourFull"
        trips={FULL_TRIPS}
        tone="cream"
        accent="#A07042"
      />

      <HomeCategoryCarousel
        testid="home-short-carousel"
        slotPrefix="home.short"
        eyebrow={COPY.short.eyebrow}
        title={COPY.short.title}
        description={COPY.short.description}
        ctaLabel={COPY.short.cta}
        ctaRouteId="tourShort"
        trips={SHORT_TRIPS}
        tone="sand"
        accent="#D97742"
      />

      <HomeCategoryCarousel
        testid="home-north-carousel"
        slotPrefix="home.north"
        eyebrow={COPY.north.eyebrow}
        title={COPY.north.title}
        description={COPY.north.description}
        ctaLabel={COPY.north.cta}
        ctaRouteId="tourNorth"
        trips={NORTH_TRIPS}
        tone="cream"
        accent="#3A4A5F"
      />

      <HomeCategoryCarousel
        testid="home-upcoming-carousel"
        slotPrefix="home.upcoming"
        eyebrow={COPY.upcoming.eyebrow}
        title={COPY.upcoming.title}
        description={COPY.upcoming.description}
        ctaLabel={COPY.upcoming.cta}
        ctaRouteId="upcomingDepartures"
        trips={UPCOMING_TRIPS}
        tone="dark"
        accent="#D4A373"
        compactMeta={true}
      />

      <StressFreeProcess />
      <WhatJourneysFeelLike />
      <div id="opiniones" className="scroll-mt-[172px]">
        <Testimonials
          themes={["general"]} limit={3} tone="cream" testid="home-testimonials"
          footer={
            <Link
              to={pathFor(lang, "opiniones")}
              data-testid="home-testimonials-all-reviews"
              className="xaluca-button inline-flex min-h-12 w-full max-w-sm items-center justify-center gap-3 border border-[#2C2621]/25 px-6 py-4 text-center text-[10px] uppercase leading-relaxed tracking-[0.24em] text-[#2C2621] transition-colors hover:border-[#C16542] hover:bg-[#C16542] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#C16542] sm:w-auto sm:px-7"
            >
              <span className="min-w-0">{REVIEWS_CTA[lang] || REVIEWS_CTA.es}</span>
              <ArrowRight className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
            </Link>
          }
        />
      </div>
      <PressMentions />
      <MoroccoVideos />
      <MoroccoCircuits />
      <MapSection />
      <PersonalConsultation />
      <CommunityCTA />
      <div id="contacto" className="scroll-mt-[172px]">
        <ContactForm />
      </div>
      <NewsletterSignup />
    </div>
  );
}
